import {useCallback, useMemo} from 'react';
import type {HttpError} from 'api/http/types';
import type {Column, Filter, ViewMode} from 'services/advancedSearch/types';
import {useLocalization} from 'localization';
import {useAdvancedSearchInfo} from 'services/advancedSearch';
import {useLazyPostExportQuery} from 'services/advancedSearch/advancedSearchApi';
import {mapPostInfoApiArgs} from 'services/advancedSearch/mappers/mapPostInfoApiArgs';
import {mapRtkErrorToHttpError} from 'store/api';
import {useSnacks} from 'components';
import type {DataFetchType} from '../types';

export const useDataFetch = (allColumns: Column[]): DataFetchType => {
  const {errorSnack} = useSnacks();
  const {t} = useLocalization();

  const {isLoading, postInfo, data} = useAdvancedSearchInfo(allColumns);
  const [downloader, {isLoading: loadingDownload}] = useLazyPostExportQuery();

  const handleDownload = useCallback(
    async (
      type: ViewMode,
      filters: Filter[],
      columns?: string[],
      distinctOn?: string,
    ) => {
      try {
        const apiArgs = mapPostInfoApiArgs(
          {type, filters, columns, distinctOn},
          allColumns,
        );

        if (!apiArgs) {
          return;
        }

        downloader(apiArgs).then(response => {
          if (response.error) {
            errorSnack(
              t('data.download.failed'),
              mapRtkErrorToHttpError(response.error),
            );
          }
        });
      } catch (error) {
        errorSnack(t('data.download.failed'), error as HttpError);
      }
    },
    [allColumns, downloader, errorSnack, t],
  );

  return useMemo(
    () => ({
      dataResponse: data,
      loadingSearch: isLoading,
      onSearch: postInfo,
      loadingDownload,
      onDownload: handleDownload,
    }),
    [data, handleDownload, isLoading, loadingDownload, postInfo],
  );
};
